import type {
  ApExceptionGroupAllocation,
  ApExceptionOwnerType,
  ApExceptionRule,
} from '@erp/shared-types';
import type {
  ApExceptionRuleSet,
  CreateApExceptionRuleSetInput,
} from '@erp/rpc';

/**
 * Editor state for one rule set. Pure helpers only — the editor component
 * renders this and calls back with the next draft; no React here so the
 * reorder / validation logic is unit-testable.
 */

export interface RuleDraft {
  /** Stable key for React lists; not persisted. */
  key: string;
  exceptionTypeOptionId: string;
  detectionGuidance: string;
  ownerType: ApExceptionOwnerType;
  ownerId: string;
  allocation: ApExceptionGroupAllocation;
  /** Task type opened per exception; '' = route on the wired hand-off task. */
  taskTypeKey: string;
}

export interface RuleSetDraft {
  name: string;
  description: string;
  exceptionEnumId: string | null;
  rules: RuleDraft[];
}

let nextKey = 0;
function newKey(): string {
  nextKey += 1;
  return `rule-${nextKey}`;
}

export function emptyRuleSetDraft(): RuleSetDraft {
  return { name: '', description: '', exceptionEnumId: null, rules: [] };
}

export function draftFromRuleSet(ruleSet: ApExceptionRuleSet): RuleSetDraft {
  return {
    name: ruleSet.name,
    description: ruleSet.description ?? '',
    exceptionEnumId: ruleSet.exceptionEnumId,
    rules: ruleSet.rules.map((rule) => ({
      key: newKey(),
      exceptionTypeOptionId: rule.exceptionTypeOptionId,
      detectionGuidance: rule.detectionGuidance ?? '',
      ownerType: rule.owner.type,
      ownerId: rule.owner.id,
      allocation: rule.owner.allocation ?? 'round_robin',
      taskTypeKey: rule.taskTypeKey ?? '',
    })),
  };
}

export function newRuleDraft(exceptionTypeOptionId = ''): RuleDraft {
  return {
    key: newKey(),
    exceptionTypeOptionId,
    detectionGuidance: '',
    ownerType: 'user',
    ownerId: '',
    allocation: 'round_robin',
    taskTypeKey: '',
  };
}

export function moveRule(
  rules: RuleDraft[],
  index: number,
  direction: -1 | 1,
): RuleDraft[] {
  const target = index + direction;
  if (index < 0 || index >= rules.length) return rules;
  if (target < 0 || target >= rules.length) return rules;
  const next = [...rules];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

/**
 * Enum options with no rule yet — the only valid picks when adding a rule
 * (each type may appear at most once). Passing the rule being edited keeps
 * its own type selectable.
 */
export function availableOptionIds(
  optionIds: readonly string[],
  rules: readonly RuleDraft[],
  editing?: RuleDraft,
): string[] {
  const taken = new Set<string>();
  for (const rule of rules) {
    if (rule.key !== editing?.key) {
      taken.add(rule.exceptionTypeOptionId);
    }
  }
  return optionIds.filter((id) => !taken.has(id));
}

/** Task type keys picked by every rule except `editing`. */
export function takenTaskTypeKeys(
  rules: readonly RuleDraft[],
  editing: RuleDraft,
): Set<string> {
  const taken = new Set<string>();
  for (const rule of rules) {
    if (rule.key !== editing.key && rule.taskTypeKey) {
      taken.add(rule.taskTypeKey);
    }
  }
  return taken;
}

/** Type to preselect on a new rule: the requested unused id, else the next unused. */
export function nextRuleOptionId(
  availableIds: readonly string[],
  requestedId?: string,
): string {
  if (requestedId !== undefined && availableIds.includes(requestedId)) {
    return requestedId;
  }
  return availableIds[0] ?? '';
}

export interface ExceptionTypeCatalogRow {
  optionId: string;
  /** 1-based rule number when this type already has a rule. */
  ruleNumber: number | null;
}

/** Every enum option, with whether a rule already claims it. */
export function exceptionTypeCatalog(
  optionIds: readonly string[],
  rules: readonly Pick<RuleDraft, 'exceptionTypeOptionId'>[],
): ExceptionTypeCatalogRow[] {
  return optionIds.map((optionId) => {
    const index = rules.findIndex(
      (rule) => rule.exceptionTypeOptionId === optionId,
    );
    return { optionId, ruleNumber: index === -1 ? null : index + 1 };
  });
}

/**
 * Rules whose type is not an option of the currently selected enum — happens
 * when the enum is swapped or an option is deleted. Saving with these fails
 * server-side, so the editor drops or flags them.
 */
export function staleRules(
  rules: readonly RuleDraft[],
  optionIds: readonly string[],
): RuleDraft[] {
  const valid = new Set(optionIds);
  return rules.filter((rule) => !valid.has(rule.exceptionTypeOptionId));
}

export function dropStaleRules(
  rules: readonly RuleDraft[],
  optionIds: readonly string[],
): RuleDraft[] {
  const valid = new Set(optionIds);
  return rules.filter((rule) => valid.has(rule.exceptionTypeOptionId));
}

export type DraftProblem =
  | 'name-required'
  | 'enum-required'
  | 'rule-type-required'
  | 'rule-owner-required'
  | 'rule-task-type-duplicate';

/** First blocking problem, or null when the draft can be saved. */
export function draftProblem(draft: RuleSetDraft): DraftProblem | null {
  if (!draft.name.trim()) return 'name-required';
  if (!draft.exceptionEnumId) return 'enum-required';
  for (const rule of draft.rules) {
    if (!rule.exceptionTypeOptionId) return 'rule-type-required';
    if (!rule.ownerId) return 'rule-owner-required';
  }
  const taskTypeKeys = new Set<string>();
  for (const rule of draft.rules) {
    if (!rule.taskTypeKey) continue;
    if (taskTypeKeys.has(rule.taskTypeKey)) return 'rule-task-type-duplicate';
    taskTypeKeys.add(rule.taskTypeKey);
  }
  return null;
}

export function canTestDraft(draft: RuleSetDraft): boolean {
  return (
    draft.exceptionEnumId !== null &&
    draft.rules.some((rule) => rule.exceptionTypeOptionId.length > 0)
  );
}

export function draftGuidanceFromRules(
  rules: readonly RuleDraft[],
): { exceptionTypeOptionId: string; detectionGuidance: string | null }[] {
  const guidance: {
    exceptionTypeOptionId: string;
    detectionGuidance: string | null;
  }[] = [];
  for (const rule of rules) {
    if (rule.exceptionTypeOptionId.length === 0) continue;
    guidance.push({
      exceptionTypeOptionId: rule.exceptionTypeOptionId,
      detectionGuidance: rule.detectionGuidance.trim() || null,
    });
  }
  return guidance;
}

function toRuleInput(rule: RuleDraft): ApExceptionRule {
  const guidance = rule.detectionGuidance.trim();
  return {
    exceptionTypeOptionId: rule.exceptionTypeOptionId,
    detectionGuidance: guidance.length > 0 ? guidance : null,
    owner: {
      type: rule.ownerType,
      id: rule.ownerId,
      ...(rule.ownerType === 'user_group'
        ? { allocation: rule.allocation }
        : {}),
    },
    taskTypeKey: rule.taskTypeKey || null,
  };
}

/** Only call after `draftProblem` returned null. */
export function toCreateInput(
  draft: RuleSetDraft,
): CreateApExceptionRuleSetInput {
  if (!draft.exceptionEnumId) {
    throw new Error('exceptionEnumId is required');
  }
  const description = draft.description.trim();
  return {
    name: draft.name.trim(),
    description: description.length > 0 ? description : null,
    exceptionEnumId: draft.exceptionEnumId,
    rules: draft.rules.map(toRuleInput),
  };
}
